import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { toast } from 'react-toastify'
import { v4 as uuidv4 } from 'uuid'

import { UseQueryOptions, UseQueryResult, useQuery, useQueryClient } from '@tanstack/react-query'

import {
    apiCreateFeatherOrder,
    apiGetFeatherOrderStatus,
    apiGetFeatherOrdersByWallet,
    apiSignFeatherQuote,
    apiUpdateFeatherOrderWithDepositDetails,
} from '@src/api/feather.api'
import { getQuote } from '@src/api/order.api'
import { ChainId } from '@src/configs/chainInfo'
import { ResultCode, StepNames } from '@src/enums'
import { featherClient } from '@src/libs/featherClient'
import { FeatherOrderStatus } from '@src/pages/Exchange/components/FeatherOrderFlow/FeatherOrderStatus'
import {
    IFeatherFormattedQuoteResponse,
    IFeatherOrderCreateResponse,
    IFeatherOrderStatusResponse,
} from '@src/types/feather'
import { IQuoteParams, IQuoteResponse } from '@src/types/order'
import { Address } from '@src/types/token'
import { isFeatherChain } from '@src/utils/chainUtils'
import { mapBackendToFrontendStatus } from '@src/utils/featherUtils'
import { createContextLogger } from '@src/utils/logger'
import { useApplicationStore, useHistoryStore } from '@src/zustand_stores'

const logger = createContextLogger('useQuote')

// Interface for the feather order callbacks
interface FeatherOrderCallbacks {
    onOrderCreated?: (orderData: IFeatherOrderCreateResponse) => void
    onOrderStatusChanged?: (orderStatus: IFeatherOrderStatusResponse) => void
}

// Interface for token data that must be supplied from the context
interface TokenData {
    symbol: string
    decimals: number
    address: string
    chainId: ChainId
    price_current?: number // Optional price for value checks
}

/**
 * A unified hook that handles both regular quotes and Feather quotes
 */
export const useQuote = (
    params: IQuoteParams | undefined,
    isQuoteEnabled: boolean,
    featherCallbacks?: FeatherOrderCallbacks,
    options?: Omit<UseQueryOptions<IQuoteResponse>, 'queryKey' | 'queryFn'>,
    // Token data must come from the context (sellData and buyData)
    sellToken?: TokenData,
    buyToken?: TokenData,
    // Additional context needed for Feather
    updateBuyData?: (updates: any) => void
) => {
    // Query client for managing cache
    const queryClient = useQueryClient()

    // Common state for both quote types
    const [isQuoteLoading, setIsQuoteLoading] = useState(false)

    // Feather specific state
    const [isCreatingOrder, setIsCreatingOrder] = useState(false)
    const [featherQuote, setFeatherQuote] = useState<IFeatherFormattedQuoteResponse | null>(null)
    const [orderId, setOrderId] = useState<string | null>(null)
    const [orderStatus, setOrderStatus] = useState<FeatherOrderStatus | null>(null)
    const [featherQuoteError, setFeatherQuoteError] = useState<string | null>(null)

    // Use a ref to track manual refresh for Feather quotes
    const manualRefreshForcedRef = useRef(false)

    // Store references
    const { setExchangeId, setCurrentModalId } = useApplicationStore()
    const { updateHistory } = useHistoryStore()

    // Determine if this is a Feather order based on the destination chain
    const isFeatherOrder = useMemo(() => {
        if (!params) return false
        const dstChain = params.dstChain
        const result = isFeatherChain(dstChain)
        return result
    }, [params])

    // Check if Feather amount is too small to bother with API calls
    const isFeatherValueTooSmall = useMemo(() => {
        if (!params || !isFeatherOrder || !sellToken) return false

        // Don't consider value too small when manual refresh is forced
        if (manualRefreshForcedRef.current) return false

        // If no sell value, it's zero (too small)
        if (!params.srcAmount || params.srcAmount === BigInt(0)) return true

        // For USD-denominated tokens, anything less than $25 is too small for Feather orders
        if (sellToken.price_current && sellToken.decimals) {
            const valueInUSD = (Number(params.srcAmount) * sellToken.price_current) / 10 ** sellToken.decimals
            // Feather requires at least $25 minimum trade size
            return valueInUSD < 25
        }

        // If we don't have price data, use a conservative token-specific threshold
        // For most ERC20s, we'll use a higher threshold equivalent to ~$25
        const minimumTokenAmount = BigInt(Math.pow(10, Math.max(0, sellToken.decimals - 1)))
        return params.srcAmount < minimumTokenAmount
    }, [params, isFeatherOrder, sellToken, manualRefreshForcedRef])

    // Regular Order Quote Hook
    const regularQuoteResult = useQuery<IQuoteResponse>({
        queryKey: ['quote', JSON.stringify(params)],
        queryFn: async ({ signal }) => {
            // NEVER run this for Feather orders
            if (isFeatherOrder) {
                return {
                    status: ResultCode.failure,
                    quote: null,
                    errorMsg: 'Regular quote canceled - this is a Feather order',
                }
            }

            if (!isQuoteEnabled) {
                logger.info('Aborting regular quote because isQuoteEnabled is false')
                throw new Error(`isQuoteEnabled is false`)
            }

            if (!params) {
                logger.error('Fetch initiated without params')
                throw new Error(`Quote parameters are required`)
            }

            if (params.srcAmount === BigInt(0)) {
                logger.error('srcAmount is 0')
                throw new Error(`Quote srcAmount is 0.`)
            }

            try {
                // Only call V2 API for regular orders
                const result = await getQuote(params, signal)

                if (result.status === ResultCode.failure) {
                    logger.error(`Error fetching quote: ${result.errorMsg ?? 'Unknown error.'}`)
                }

                return result
            } catch (error) {
                if (error instanceof Error && error.name === 'AbortError') {
                    logger.info('Regular quote query was forcefully cancelled.')
                }
                const errorMessage = error instanceof Error ? error.message : 'An unknown error occurred'
                return {
                    status: ResultCode.failure,
                    quote: null,
                    errorMsg: errorMessage,
                }
            }
        },
        enabled:
            isQuoteEnabled &&
            !isFeatherOrder && // Never run regular quote for Feather orders
            !!params &&
            params.srcAmount !== BigInt(0),
        refetchOnMount: true,
        notifyOnChangeProps: ['data', 'error', 'isLoading', 'isFetching'],
        gcTime: 1000,
        initialData: {
            status: ResultCode.pending,
            quote: null,
            errorMsg: 'Uninitialized query call.',
        },
        ...options,
    })

    // Feather Quote Hook
    const featherQuoteResult = useQuery({
        queryKey: [
            'featherQuote',
            params?.srcChain,
            params?.srcAssetAddress,
            params?.dstChain,
            params?.srcAmount?.toString(),
            params?.targetAddress,
            // Add a manual refresh flag to force refetch via ref
            manualRefreshForcedRef.current ? 'manual-refresh' : null,
        ],
        queryFn: async () => {
            // Skip if conditions aren't met
            if (!params) {
                return { error: 'No parameters provided' }
            }

            if (!params.srcAmount || params.srcAmount === BigInt(0)) {
                return { error: 'No amount specified' }
            }

            if (!sellToken || !buyToken) {
                return { error: 'Missing token data' }
            }

            if (isFeatherValueTooSmall) {
                logger.info('Skipping Feather API call - amount likely too small', {
                    value: params.srcAmount.toString(),
                })
                return {
                    error: 'Trade amount too small. Please increase your trade amount to at least $25.',
                    errorType: 'amount_too_small',
                }
            }

            if (!params.targetAddress || params.targetAddress === '') {
                logger.info('Skipping Feather API call - no destination address')
                return {
                    error: 'Please add a recipient address.',
                    errorType: 'missing_address',
                }
            }

            // Reset any previous errors
            setFeatherQuoteError(null)
            setIsQuoteLoading(true)

            try {
                const quote = await featherClient.getFeatherQuote(
                    sellToken.chainId, // Use the chainId from sellToken
                    params.srcAssetAddress, // Source token address from params
                    params.srcAmount.toString(), // Source amount from params
                    buyToken.chainId, // Use the chainId from buyToken
                    params.targetAddress as Address, // Target address from params
                    sellToken.symbol, // Token symbol from sellToken
                    sellToken.decimals, // Token decimals from sellToken
                    buyToken.symbol, // Token symbol from buyToken
                    buyToken.decimals, // Token decimals from buyToken
                    params.walletAddress // Wallet address from params
                )

                // Store the Feather quote
                setFeatherQuote({
                    ...quote,
                    buyAmount: quote.buyAmount,
                })

                // Reset manual refresh flag
                if (manualRefreshForcedRef.current) {
                    manualRefreshForcedRef.current = false
                }

                // Update buy amount if updateBuyData is provided
                if (updateBuyData && quote.buyAmount) {
                    try {
                        const buyAmountBigInt = BigInt(quote.buyAmount)
                        updateBuyData({ value: buyAmountBigInt })
                    } catch (error) {
                        logger.error('Failed to parse Feather quote buy amount', error)
                    }
                }

                return quote
            } catch (error: any) {
                // Handle error from Feather API
                let errorMessage = 'Failed to fetch Feather quote'
                let errorType = 'unknown'

                if (error.response?.data?.detail) {
                    errorMessage = error.response.data.detail
                    if (errorMessage.includes('Trade amount too small')) {
                        errorType = 'amount_too_small'
                        errorMessage = 'Trade amount too small. Please increase your trade amount.'
                    }
                } else if (error.response?.data?.message) {
                    errorMessage = error.response.data.message
                } else if (error.message) {
                    errorMessage = error.message
                }

                logger.error('Feather quote error:', {
                    error,
                    message: errorMessage,
                    errorType,
                })

                setFeatherQuoteError(errorMessage)
                return {
                    error: errorMessage,
                    errorType,
                }
            } finally {
                setIsQuoteLoading(false)
            }
        },
        enabled:
            isQuoteEnabled &&
            isFeatherOrder && // Only run for Feather orders
            !!params &&
            params.srcAmount !== BigInt(0) &&
            !isFeatherValueTooSmall &&
            !!params.targetAddress &&
            !!sellToken && // Need sell token data
            !!buyToken, // Need buy token data
        refetchOnMount: true,
        staleTime: 15000, // 15 seconds
        gcTime: 30000, // 30 seconds
        retry: 2,
        retryDelay: 1000,
    })

    // Function to manually get a Feather quote (for compatibility with existing code)
    const getFeatherQuote = useCallback(async () => {
        if (!isFeatherOrder) {
            logger.warn('Attempted to get Feather quote for non-Feather order')
            return null
        }

        logger.info('Manually fetching Feather quote')
        manualRefreshForcedRef.current = true
        const result = await featherQuoteResult.refetch()
        return result.data
    }, [featherQuoteResult, isFeatherOrder])

    // Create a Feather order
    const createFeatherOrder = useCallback(async () => {
        if (!params || !isFeatherOrder || !featherQuote) {
            logger.error('Cannot create Feather order: missing required data')
            return null
        }

        if (!params.targetAddress) {
            logger.error('Cannot create order: no destination address')
            setFeatherQuoteError('Please add a recipient address.')
            return null
        }

        try {
            setIsCreatingOrder(true)

            // In real implementation, you'd use the featherQuoteId from the quote
            const featherQuoteId = featherQuote.quoteId || ''

            logger.info('Creating Feather order with:', { quoteId: featherQuoteId, wallet: params.walletAddress })

            // Create a new exchange ID for tracking
            const newExchangeId = uuidv4()
            setExchangeId(newExchangeId)
            setCurrentModalId(newExchangeId)

            // Call API to create order
            const orderData = await apiCreateFeatherOrder(featherQuoteId, params.walletAddress)

            logger.info('Received Feather order response:', orderData)

            // Update order state
            setOrderId(orderData.orderId)
            setOrderStatus(FeatherOrderStatus.AWAITING_DEPOSIT)

            // Update history for tracking (simplified - actual implementation would need sellData and buyData)
            updateHistory(newExchangeId, {
                orderId: orderData.orderId,
                currentStep: StepNames.FEATHER_WAITING_FOR_DEPOSIT,
            })

            // Notify caller if callback provided
            if (featherCallbacks?.onOrderCreated) {
                featherCallbacks.onOrderCreated(orderData)
            }

            return orderData
        } catch (error) {
            logger.error('Error creating Feather order:', error)
            return null
        } finally {
            setIsCreatingOrder(false)
        }
    }, [params, isFeatherOrder, featherQuote, featherCallbacks, setExchangeId, setCurrentModalId, updateHistory])

    // Check Feather order status
    const checkFeatherOrderStatus = useCallback(
        async (orderIdToCheck: string) => {
            try {
                const status = await apiGetFeatherOrderStatus(orderIdToCheck)
                // Convert backend status to frontend status
                setOrderStatus(mapBackendToFrontendStatus(status.status))

                if (featherCallbacks?.onOrderStatusChanged) {
                    featherCallbacks.onOrderStatusChanged(status)
                }

                return status
            } catch (error: any) {
                logger.error('Error checking order status:', error)
                return null
            }
        },
        [featherCallbacks]
    )

    // Update Feather order with deposit transaction
    const updateFeatherOrderWithDeposit = useCallback(
        async (depositTxHash: string) => {
            if (!orderId) {
                toast.error('No active order to update')
                return null
            }

            try {
                const updatedStatus = await apiUpdateFeatherOrderWithDepositDetails(orderId, depositTxHash)
                // Convert backend status to frontend status
                setOrderStatus(mapBackendToFrontendStatus(updatedStatus.status))

                if (featherCallbacks?.onOrderStatusChanged) {
                    featherCallbacks.onOrderStatusChanged(updatedStatus)
                }

                return updatedStatus
            } catch (error: any) {
                logger.error('Error updating order with deposit:', error)
                toast.error('Failed to update order with deposit transaction')
                return null
            }
        },
        [orderId, featherCallbacks]
    )

    // Get Feather order history
    const getFeatherOrderHistory = useCallback(async (walletAddress: string) => {
        try {
            return await apiGetFeatherOrdersByWallet(walletAddress)
        } catch (error: any) {
            logger.error('Error getting order history:', error)
            return []
        }
    }, [])

    // Sign a Feather quote
    const signFeatherQuote = useCallback(async (quoteId: string, signature: string, srcUserWallet: string) => {
        try {
            return await apiSignFeatherQuote(quoteId, signature, srcUserWallet)
        } catch (error: any) {
            logger.error('Error signing Feather quote:', error)
            throw error
        }
    }, [])

    // Forcefully cancel an ongoing quote query
    const forceCancel = useCallback(async (): Promise<void> => {
        logger.info('Force cancelling the quote query')

        if (isFeatherOrder) {
            await queryClient.cancelQueries({
                queryKey: ['featherQuote'],
            })
        } else {
            await queryClient.cancelQueries({
                queryKey: ['quote'],
            })
        }
    }, [queryClient, isFeatherOrder])

    // Invalidate current quote and refetch with new parameters
    const invalidateQuoteAndRefetch = useCallback(
        async (newParams: IQuoteParams): Promise<void> => {
            logger.info('Invalidating and refetching', { newParams })

            if (!isQuoteEnabled) {
                logger.info('Aborting invalidateQuoteAndRefetch because isQuoteEnabled is false')
                return
            }

            const newIsFeatherOrder = isFeatherChain(newParams.dstChain)

            if (newIsFeatherOrder) {
                // For Feather orders
                await queryClient.invalidateQueries({ queryKey: ['featherQuote'] })
                manualRefreshForcedRef.current = true

                // Trigger a refresh by forcing a new query
                await queryClient.refetchQueries({
                    queryKey: [
                        'featherQuote',
                        newParams.srcChain,
                        newParams.srcAssetAddress,
                        newParams.dstChain,
                        newParams.srcAmount?.toString(),
                        newParams.targetAddress,
                        'manual-refresh',
                    ],
                })
            } else {
                // For regular orders
                await queryClient.invalidateQueries({ queryKey: ['quote'] })
                queryClient.setQueryData(['quote', JSON.stringify(newParams)], undefined)

                // Prefetch the new quote
                await queryClient.prefetchQuery({
                    queryKey: ['quote', JSON.stringify(newParams)],
                    queryFn: async () => await getQuote(newParams),
                })
            }
        },
        [queryClient, isQuoteEnabled]
    )

    // Set a manual refresh flag for Feather quotes
    const setManualRefreshForced = useCallback((value: boolean) => {
        manualRefreshForcedRef.current = value
    }, [])

    // Consolidated loading state
    useEffect(() => {
        const isRegularLoading = regularQuoteResult.isLoading || regularQuoteResult.isFetching
        const isFeatherLoading = featherQuoteResult.isLoading || featherQuoteResult.isFetching || isCreatingOrder

        setIsQuoteLoading((isFeatherOrder && isFeatherLoading) || (!isFeatherOrder && isRegularLoading))
    }, [
        isFeatherOrder,
        regularQuoteResult.isLoading,
        regularQuoteResult.isFetching,
        featherQuoteResult.isLoading,
        featherQuoteResult.isFetching,
        isCreatingOrder,
    ])

    // Prepare unified response data
    const responseData = useMemo(() => {
        if (isFeatherOrder) {
            // Format Feather quote response to match IQuoteResponse structure
            const featherData = featherQuoteResult.data

            // If we have an error from Feather
            if (featherData && 'error' in featherData) {
                return {
                    status: ResultCode.failure,
                    quote: null,
                    errorMsg: featherData.error,
                }
            }

            // If we have a successful Feather quote (has quoteId)
            if (featherData && 'quoteId' in featherData && featherData.quoteId) {
                // For Feather quotes, just include the featherQuoteId and isFeatherOrder flag
                // Let the UI handle the rest using the data from the context
                return {
                    status: ResultCode.success,
                    quote: {
                        featherQuoteId: featherData.quoteId,
                        isFeatherOrder: true,
                    },
                    errorMsg: null,
                }
            }

            // Default pending state for Feather
            return {
                status: ResultCode.pending,
                quote: null,
                errorMsg: null,
            }
        } else {
            // Return regular quote response - don't modify this path
            return (
                regularQuoteResult.data || {
                    status: ResultCode.failure,
                    quote: null,
                    errorMsg: 'Uninitialized regular quote call.',
                }
            )
        }
    }, [isFeatherOrder, featherQuoteResult.data, regularQuoteResult.data])

    // Return unified interface with type-safe guarantees
    return {
        // Common properties for all order types
        data: responseData,
        isLoading: isQuoteLoading,
        error: isFeatherOrder ? featherQuoteError : regularQuoteResult.error,
        refetch: isFeatherOrder ? featherQuoteResult.refetch : regularQuoteResult.refetch,
        isFetching: isFeatherOrder ? featherQuoteResult.isFetching : regularQuoteResult.isFetching,
        isRefetching: isFeatherOrder ? featherQuoteResult.isRefetching : regularQuoteResult.isRefetching,

        // Common methods
        forceCancel,
        invalidateQuoteAndRefetch,

        getQuote: getFeatherQuote,

        // Feather-specific properties and methods
        isFeatherOrder,
        featherQuote,
        featherQuoteError,
        orderId,
        orderStatus,
        isCreatingOrder,
        createFeatherOrder,
        checkFeatherOrderStatus,
        updateFeatherOrderWithDeposit,
        getFeatherOrderHistory,
        signFeatherQuote,
        setManualRefreshForced,
    } as UseQueryResult<IQuoteResponse> & {
        // Ensure data is always of type IQuoteResponse
        data: IQuoteResponse

        // Common methods
        invalidateQuoteAndRefetch: (newParams: IQuoteParams) => Promise<void>
        forceCancel: () => Promise<void>

        getQuote: () => Promise<any>

        // Feather-specific properties
        isFeatherOrder: boolean
        featherQuote: IFeatherFormattedQuoteResponse | null
        featherQuoteError: string | null
        orderId: string | null
        orderStatus: FeatherOrderStatus | null
        isCreatingOrder: boolean
        createFeatherOrder: () => Promise<IFeatherOrderCreateResponse | null>
        checkFeatherOrderStatus: (orderId: string) => Promise<IFeatherOrderStatusResponse | null>
        updateFeatherOrderWithDeposit: (txHash: string) => Promise<IFeatherOrderStatusResponse | null>
        getFeatherOrderHistory: (walletAddress: string) => Promise<any[]>
        signFeatherQuote: (quoteId: string, signature: string, srcUserWallet: string) => Promise<any>
        setManualRefreshForced: (value: boolean) => void
    }
}
